import React, { useState } from "react";
import type { KumaHeartbeat } from "../types";
import { formatHungarianDateTime } from "../utils";

interface HeartbeatBarProps {
  heartbeats: KumaHeartbeat[];
  uptimePercent: number;
  totalSegments?: number;
}

export const HeartbeatBar: React.FC<HeartbeatBarProps> = ({
  heartbeats,
  uptimePercent,
  totalSegments = 40,
}) => {
  const [hoveredBeat, setHoveredBeat] = useState<{
    beat: KumaHeartbeat | null;
    index: number;
  } | null>(null);

  let displayBeats: (KumaHeartbeat | null)[] = [];
  if (heartbeats.length >= totalSegments) {
    displayBeats = heartbeats.slice(-totalSegments);
  } else if (heartbeats.length > 0) {
    const padCount = totalSegments - heartbeats.length;
    displayBeats = [...Array(padCount).fill(null), ...heartbeats];
  } else {
    displayBeats = Array(totalSegments).fill(null);
  }

  const getStatusColor = (beat: KumaHeartbeat | null) => {
    if (!beat) return "bg-[#dedbd4] hover:bg-[#c9c5bd]";
    switch (beat.status) {
      case 1:
        return "bg-[#246047] hover:bg-[#1a4a36]";
      case 0:
        return "bg-[#922d35] hover:bg-[#722026]";
      case 2:
        return "bg-[#c5a880] hover:bg-[#b5956a]";
      case 3:
        return "bg-[#365570] hover:bg-[#284055]";
      default:
        return "bg-[#8c8982] hover:bg-[#737069]";
    }
  };

  const getStatusLabelHu = (status?: number) => {
    switch (status) {
      case 1:
        return "Zavartalan";
      case 0:
        return "Kimaradás / Hiba";
      case 2:
        return "Várakozó ellenőrzés";
      case 3:
        return "Karbantartás";
      default:
        return "Nincs adat";
    }
  };

  return (
    <div className="w-full flex flex-col gap-1.5">
      {/* Segmented Heartbeat Bar */}
      <div className="relative flex items-center gap-1 sm:gap-1.5 w-full h-7 px-0.5">
        {displayBeats.map((beat, idx) => (
          <div
            key={idx}
            onMouseEnter={() => setHoveredBeat({ beat, index: idx })}
            onMouseLeave={() => setHoveredBeat(null)}
            className={`flex-1 h-6 rounded-[2px] transition-transform duration-100 cursor-pointer ${getStatusColor(
              beat
            )} ${hoveredBeat?.index === idx ? "scale-y-125 z-20 ring-1 ring-[#1b2a47]" : ""}`}
          />
        ))}

        {/* Hover Tooltip */}
        {hoveredBeat && (
          <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-30 pointer-events-none whitespace-nowrap px-2.5 py-1 rounded-[0.375rem] bg-white border border-[#dedbd4] shadow-md text-xs flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full ${
                hoveredBeat.beat?.status === 1
                  ? "bg-[#246047]"
                  : hoveredBeat.beat?.status === 0
                  ? "bg-[#922d35]"
                  : "bg-[#8c8982]"
              }`}
            />
            <span className="text-[#1b2a47] font-semibold">
              {getStatusLabelHu(hoveredBeat.beat?.status)}
            </span>
            {typeof hoveredBeat.beat?.ping === "number" &&
              Number.isFinite(hoveredBeat.beat.ping) && (
                <span className="text-[#62666b] font-mono">
                  {hoveredBeat.beat.ping.toFixed(1)} ms
                </span>
              )}
            <span className="text-[#8c8982] text-[11px]">
              {hoveredBeat.beat ? formatHungarianDateTime(hoveredBeat.beat.time) : "Korábbi időszak"}
            </span>
          </div>
        )}
      </div>

      {/* Bar Labels */}
      <div className="flex items-center justify-between text-[11px] text-[#8c8982]">
        <span>Előzmények</span>
        <span className="font-mono text-[#62666b] font-medium">
          {uptimePercent.toFixed(2)}% rendelkezésre állás
        </span>
        <span>Ma</span>
      </div>
    </div>
  );
};
